import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Input from '../Atoms/Input';

// Styled components for the dropzone
const DropzoneContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const DropzoneArea = styled.div`
  border: 2px dashed ${props => props.isDragActive ? '#0000FF' : '#ccc'};
  padding: 30px;
  text-align: center;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${props => props.isDragActive ? 'rgba(0, 0, 255, 0.05)' : '#f8f8f8'};
  border-radius: 4px;
  
  &:hover {
    border-color: #0000FF;
    background-color: rgba(0, 0, 255, 0.05);
  }
`;

const UploadIcon = styled.div`
  margin-bottom: 15px;
  font-size: 36px;
  color: #666;
`;

const UploadText = styled.div`
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
`;

const UploadSubText = styled.div`
  font-size: 14px;
  color: #666;
`;

const MediaPreviewGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 20px;
`;

const MediaUploadForm = styled.div`
  margin-top: 15px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const FileContainer = styled.div`
  width: 200px;
  margin-bottom: 20px;

  .mainMedia {
    width: 100%;
    height: 150px;
    border: 1px solid #ddd;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
    border-radius: 4px;
    background-color: white;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .remove_cross {
      position: absolute;
      top: 5px;
      right: 5px;
      background-color: red;
      color: white;
      font-size: 16px;
      border-radius: 50%;
      width: 25px;
      height: 25px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      opacity: 0.7;
      z-index: 10;
      transition: opacity 0.2s ease;
    }

    &:hover .remove_cross {
      opacity: 1;
    }
  }

  .fileName {
    font-size: 12px;
    margin-top: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .credit {
    font-size: 12px;
    margin-top: 2px;
  }
`;

const SubmitButton = styled.button`
  background-color: #0000FF;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-top: 10px;
  
  &:hover {
    background-color: #0000CC;
  }
  
  &:disabled {
    background-color: #CCCCCC;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 5px;
`;

const MediaUploadWithDropzone = ({ onAddMedia, maxSizeMB = 50 }) => {
  const { i18n } = useTranslation('common');
  const [isDragActive, setIsDragActive] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [credit, setCredit] = useState('');
  const [legende, setLegende] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragActive) {
      setIsDragActive(true);
    }
  }, [isDragActive]);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      handleFileSelection(file);
    }
  }, []);
  
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleFileSelection(file);
    }
  };
  
  const handleFileSelection = (file) => {
    setError('');
    
    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(i18n.language === "fr" 
        ? `Le fichier est trop lourd. Veuillez sélectionner un fichier de moins de ${maxSizeMB}Mo.` 
        : `The file is too heavy. Please select a file under ${maxSizeMB}Mb.`);
      return;
    }
    
    // Check file type
    const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'video/mp4', 'video/quicktime'];
    if (!acceptedTypes.includes(file.type)) {
      setError(i18n.language === "fr" 
        ? "Format de fichier non supporté. Veuillez sélectionner un fichier .jpg, .png, .pdf, .mp4 ou .mov." 
        : "Unsupported file format. Please select a .jpg, .png, .pdf, .mp4 or .mov file.");
      return;
    }
    
    setCurrentFile(file);
  };
  
  const handleCreditChange = (e) => {
    setCredit(e.target.value);
  };
  
  const handleLegendeChange = (e) => {
    setLegende(e.target.value);
  };
  
  const handleAddMedia = () => {
    if (!currentFile) {
      setError(i18n.language === "fr" 
        ? "Veuillez sélectionner un fichier." 
        : "Please select a file.");
      return;
    }
    
    if (credit === '') {
      setError(i18n.language === "fr" 
        ? "Veuillez renseigner le crédit de la photo." 
        : "Please provide the photo credit.");
      return;
    }
    
    onAddMedia({ file: currentFile, credit, legende });
    
    // Reset form
    setCurrentFile(null);
    setCredit('');
    setLegende('');
    setError('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleCancelUpload = () => {
    setCurrentFile(null);
    setCredit('');
    setLegende('');
    setError('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const renderFilePreview = () => {
    if (!currentFile) return null;
    
    const url = URL.createObjectURL(currentFile);
    
    return (
      <FileContainer>
        <div className="mainMedia">
          <div className="remove_cross" onClick={handleCancelUpload}>✕</div>
          {currentFile.type.includes('image') ? (
            <img src={url} alt={currentFile.name} />
          ) : currentFile.type.includes('video') ? (
            <video controls>
              <source src={url} type={currentFile.type} />
              {i18n.language === 'fr' ? 'Votre navigateur ne supporte pas la vidéo.' : 'Your browser does not support the video tag.'}
            </video>
          ) : currentFile.type.includes('pdf') ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
              <img src="/img/pdf-icon.png" alt="PDF" style={{ width: '50px', height: '50px' }} />
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '10px' }}>
                {i18n.language === 'fr' ? 'Voir le PDF' : 'View PDF'}
              </a>
            </div>
          ) : (
            <div>{currentFile.name}</div>
          )}
        </div>
        <div className="fileName">{currentFile.name}</div>
      </FileContainer>
    );
  };
  
  return (
    <DropzoneContainer>
      {!currentFile ? (
        <DropzoneArea 
          isDragActive={isDragActive}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          <UploadIcon>
            <span>📁</span>
          </UploadIcon>
          <UploadText>
            {i18n.language === 'fr' 
              ? 'Glissez et déposez un fichier ici, ou cliquez pour sélectionner un fichier' 
              : 'Drag and drop a file here, or click to select a file'}
          </UploadText>
          <UploadSubText>
            {i18n.language === "fr" 
              ? "(.mp4, .pdf, .png, .jpg ou .jpeg, 50Mo maximum)" 
              : "(.mp4, .pdf, .png, .jpg or .jpeg, 50Mb maximum)"}
          </UploadSubText>
        </DropzoneArea>
      ) : (
        <MediaUploadForm>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>{renderFilePreview()}</div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {i18n.language === "fr" ? "Crédit *" : "Credit *"}
              </label>
              <Input 
                style={{ width: "100%" }} 
                required 
                onChange={handleCreditChange} 
                placeholder={i18n.language === "fr" ? "Crédit" : "Credit"} 
                value={credit} 
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                {i18n.language === "fr" ? "Légende" : "Caption"}
              </label>
              <Input 
                style={{ width: "100%" }} 
                onChange={handleLegendeChange} 
                placeholder={i18n.language === "fr" ? "Légende" : "Caption"} 
                value={legende} 
              />
            </div>
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={handleCancelUpload}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
              <SubmitButton onClick={handleAddMedia}>
                {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
              </SubmitButton>
            </div>
          </div>
        </MediaUploadForm>
      )}
      
      <input 
        hidden 
        type="file" 
        accept="application/pdf, image/png, image/jpeg, video/mp4, video/quicktime" 
        onChange={handleFileInputChange} 
        ref={fileInputRef} 
      />
    </DropzoneContainer>
  );
};

// Media Preview component that can be used for both new and existing media
const MediaPreviewItem = ({ media, isNew = false, onDelete }) => {
  const { i18n } = useTranslation('common');
  
  const fileObj = isNew ? media.file : media;
  if (!fileObj) return null;
  
  const fullPath = isNew 
    ? URL.createObjectURL(fileObj) 
    : `${process.env.REACT_APP_MEDIA_BASE_URL}userfiles/${fileObj.path}`;
  
  const fileName = isNew ? fileObj.name : (fileObj.fileName || fileObj.name || '');
  const credit = media.credit || '';
  
  return (
    <FileContainer>
      <div className="mainMedia">
        {onDelete && <div className="remove_cross" onClick={() => onDelete(media)}>✕</div>}
        {fileObj.type && fileObj.type.includes('image') ? (
          <img src={fullPath} alt={fileName} />
        ) : fileObj.type && fileObj.type.includes('video') ? (
          <video controls>
            <source src={fullPath} type={fileObj.type} />
            {i18n.language === 'fr' ? 'Votre navigateur ne supporte pas la vidéo.' : 'Your browser does not support the video tag.'}
          </video>
        ) : fileObj.type && fileObj.type.includes('pdf') ? (
          <a href={fullPath} target="_blank" rel="noopener noreferrer">
            {i18n.language === 'fr' ? 'Voir le PDF' : 'View PDF'}
          </a>
        ) : (
          <div>{fileName}</div>
        )}
      </div>
      <div className="fileName">{fileName}</div>
      <div className="credit">{i18n.language === 'fr' ? 'Crédit:' : 'Credit:'} {credit}</div>
    </FileContainer>
  );
};

export { MediaUploadWithDropzone, MediaPreviewItem };